import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import {
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { type JSX, useCallback, useMemo, useState } from 'react'
import EmptyState from '~/components/empty-state'
import { useDatastore } from '~/features/datastore'
import { Table } from '~/features/table/ui/table'
import { useTRPC } from '~/services/trpc'
import {
  DEFAULT_WORKSPACE_FILTER_VALUE,
  SettingsActions,
  SettingsContent,
  useSettingTitle,
} from '~/templates/settings'

import { FilterCollection } from '@hap/analytics'
import {
  Button,
  Icon,
  Search,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@hap/components'
import { get } from '@hap/utils'

import { TOPIC_FALLBACK_DATA, columns } from './-components/table-config'

export const Route = createFileRoute('/_authenticated/$alias/settings/topics/')({
  component: TopicsSettings,
})

function TopicsSettings(): JSX.Element {
  const trpc = useTRPC()
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const search = location.search as Record<string, unknown>
  const query = (search.query as string) || ''
  const filters = (search.filters as Record<string, unknown>) || {}

  const setQuery = useCallback(
    (value: string | undefined) => {
      navigate({
        to: location.pathname,
        search: {
          ...search,
          query: value,
          page: 1,
        },
        replace: true,
      })
    },
    [location.pathname, navigate, search],
  )

  const updateFilters = useCallback(
    (filterUpdates: Record<string, unknown>) => {
      navigate({
        to: location.pathname,
        search: {
          ...search,
          filters: { ...filters, ...filterUpdates },
          page: 1,
        },
        replace: true,
      })
    },
    [filters, location.pathname, navigate, search],
  )
  const { data: workspaces } = useDatastore(FilterCollection.Workspaces)
  const [newTopic, setNewTopic] = useState<
    | false
    | {
        id: string
        label: string
        workspaceId: string | null
        isActive: boolean
        suggested: string | null
        createdAt: string
        updatedAt: string
        editing: boolean
        onSubmit: () => void
      }
  >(false)

  useSettingTitle([
    {
      label: 'Topics',
      description:
        'Topics represent the main subjects discussed in customer conversations.',
    },
  ])

  const workspaceFilter = String(
    get(filters, 'workspaceId', DEFAULT_WORKSPACE_FILTER_VALUE),
  )

  const { data, isLoading } = useQuery(trpc.topics.list.queryOptions())

  const [sort, setSort] = useState<SortingState>([{ id: 'label', desc: false }])

  const topics = useMemo(() => {
    if (!data) return TOPIC_FALLBACK_DATA
    let filtered = data

    // Apply search filter
    if (query) {
      filtered = filtered.filter((topic) =>
        topic.label.toLowerCase().includes(query.toLowerCase()),
      )
    }

    // Apply workspace filter
    if (workspaceFilter === 'Organization') {
      filtered = filtered.filter((topic) => !topic.workspaceId)
    } else if (workspaceFilter !== 'All') {
      filtered = filtered.filter((topic) => topic.workspaceId === workspaceFilter)
    }

    // Apply status filter
    // if (statusFilter === 'confirmed') {
    //   filtered = filtered.filter((topic) => topic.isActive)
    // } else if (statusFilter === 'suggested') {
    //   filtered = filtered.filter((topic) => !topic.isActive)
    // }

    return filtered
  }, [data, query, workspaceFilter])

  const onSubmit = useCallback(() => {
    setNewTopic(false)
  }, [])

  const handleAddTopic = useCallback(() => {
    setNewTopic({
      id: '',
      label: '',
      workspaceId: null,
      isActive: true,
      suggested: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      editing: true,
      onSubmit,
    })
  }, [onSubmit])

  const tableData = useMemo(() => {
    return newTopic ? [newTopic, ...topics] : topics
  }, [newTopic, topics])

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    onSortingChange: setSort,
    state: {
      sorting: sort,
    },
    defaultColumn: {
      size: 0,
      minSize: 0,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
  })

  return (
    <>
      <SettingsActions>
        <Button onClick={handleAddTopic}>Add topic</Button>
      </SettingsActions>
      <SettingsContent spacing="sm">
        <div className="flex justify-between gap-3">
          <div className="flex items-center gap-3">
            <Search
              placeholder="Search by name"
              onSearch={(value) => setQuery(value || undefined)}
              className="flex w-125 max-w-md shrink-0 grow"
              defaultValue={query}
            />

            <Select
              value={workspaceFilter}
              onValueChange={(value) => updateFilters({ workspaceId: value })}
            >
              <SelectTrigger
                size="sm-wd"
                className="w-50 shrink-0"
                variant="ghost"
                data-isdirty={workspaceFilter !== DEFAULT_WORKSPACE_FILTER_VALUE}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All workspaces</SelectItem>
                <SelectItem value="Organization">Organization-wide</SelectItem>
                {workspaces?.map((workspace) => (
                  <SelectItem key={workspace.id} value={workspace.id}>
                    {workspace.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {data?.length === 0 && !newTopic ? (
          <EmptyState
            icon={<Icon name="chats-circle" size={32} weight="fill" />}
            title="Topics"
            description="Topics represent the main subjects discussed in customer conversations. They help categorize and analyze what customers are talking about."
            actions={[{ label: 'Add topic', onClick: handleAddTopic }]}
          />
        ) : (
          <Table table={table} isLoading={isLoading} />
        )}
      </SettingsContent>
    </>
  )
}
